import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, Search, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { productsAPI, categoriesAPI, makesAPI, adminAPI } from '../../services/api';
import { toast } from 'sonner';

const PdfUploadButton = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadPdf(file);
      onUploaded(res.url);
      toast.success('PDF uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PDF upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf" onChange={handleChange} className="hidden" data-testid="pdf-upload-input" />
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-pdf-btn">
        <Upload className="h-3 w-3 mr-1" /> {uploading ? 'Uploading…' : 'Upload PDF'}
      </Button>
    </>
  );
};

const ImageUploadButton = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      onUploaded(res.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleChange} className="hidden" />
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-image-btn">
        <Upload className="h-3 w-3 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
      </Button>
    </>
  );
};

const EMPTY_PRODUCT = {
  sku: '',
  name: '', make: '', model: '', engine: '', cc: 0,
  stockPower: 0, stockTorque: 0, gainPower: 0, gainTorque: 0,
  category: 'pro-petrol', price: 0, currency: 'AUD',
  image: '/images/module-5.webp', description: '',
  features: [
    'Plug & Play Installation',
    'Warranty Friendly',
    'Adjustable Settings',
    'Traceless Removal',
    '2 Year Warranty',
  ],
  inStock: true,
  instructionsUrl: '', unbrandedInstructionsUrl: '', instructionsId: '',
  connections: '', railMap: '', boost: '', cam: '',
  fuelSystem: '', engineId: '', productUrl: '',
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 border-b pb-1">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, span = 1, children }) => (
  <div className={span === 2 ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-medium mb-1">{label}</label>
    {children}
  </div>
);

const ProductForm = ({ value, onChange, categories, makes }) => {
  const updateFeature = (idx, v) => {
    const features = [...(value.features || [])];
    features[idx] = v;
    onChange({ ...value, features });
  };
  const addFeature = () => onChange({ ...value, features: [...(value.features || []), ''] });
  const removeFeature = (idx) => onChange({ ...value, features: value.features.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-5">
      <Section title="Basic Info">
        <Field label="SKU">
          <Input
            value={value.sku || ''}
            onChange={(e) => onChange({ ...value, sku: e.target.value })}
            placeholder="e.g. BS-BMW-320d-184"
            data-testid="product-form-sku"
          />
        </Field>
        <Field label="Product Name">
          <Input value={value.name || ''} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="product-form-name" />
        </Field>
        <Field label="Make">
          <Input list="makes-list" value={value.make || ''} onChange={(e) => onChange({ ...value, make: e.target.value })} />
          <datalist id="makes-list">{makes.map((m) => <option key={m} value={m} />)}</datalist>
        </Field>
        <Field label="Model">
          <Input value={value.model || ''} onChange={(e) => onChange({ ...value, model: e.target.value })} />
        </Field>
        <Field label="Engine">
          <Input value={value.engine || ''} onChange={(e) => onChange({ ...value, engine: e.target.value })} />
        </Field>
        <Field label="Engine CC">
          <Input type="number" value={value.cc || 0} onChange={(e) => onChange({ ...value, cc: Number(e.target.value) })} />
        </Field>
        <Field label="Fuel System">
          <Input value={value.fuelSystem || ''} placeholder="e.g. Common Rail" onChange={(e) => onChange({ ...value, fuelSystem: e.target.value })} />
        </Field>
        <Field label="Engine ID">
          <Input value={value.engineId || ''} onChange={(e) => onChange({ ...value, engineId: e.target.value })} />
        </Field>
        <Field label="Category">
          <Select value={value.category} onValueChange={(v) => onChange({ ...value, category: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="In Stock">
          <Select value={String(value.inStock)} onValueChange={(v) => onChange({ ...value, inStock: v === 'true' })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </Section>

      <Section title="Pricing">
        <Field label="Price (AUD)">
          <Input type="number" step="0.01" value={value.price || 0} onChange={(e) => onChange({ ...value, price: Number(e.target.value) })} data-testid="product-form-price" />
        </Field>
        <Field label="Currency">
          <Input value={value.currency || 'AUD'} onChange={(e) => onChange({ ...value, currency: e.target.value })} />
        </Field>
      </Section>

      <Section title="Performance Gains">
        <Field label="Stock Power (PS)">
          <Input type="number" value={value.stockPower || 0} onChange={(e) => onChange({ ...value, stockPower: Number(e.target.value) })} />
        </Field>
        <Field label="Stock Torque (Nm)">
          <Input type="number" value={value.stockTorque || 0} onChange={(e) => onChange({ ...value, stockTorque: Number(e.target.value) })} />
        </Field>
        <Field label="Power Gain (PS)">
          <Input type="number" value={value.gainPower || 0} onChange={(e) => onChange({ ...value, gainPower: Number(e.target.value) })} />
        </Field>
        <Field label="Torque Gain (Nm)">
          <Input type="number" value={value.gainTorque || 0} onChange={(e) => onChange({ ...value, gainTorque: Number(e.target.value) })} />
        </Field>
      </Section>

      <Section title="Imagery & Description">
        <Field label="Main Product Image (carousel slide 1)" span={2}>
          <div className="flex gap-2">
            <Input
              value={value.image || ''}
              onChange={(e) => onChange({ ...value, image: e.target.value })}
              placeholder="https://… or /uploads/images/…"
              data-testid="product-form-image"
            />
            <ImageUploadButton onUploaded={(url) => onChange({ ...value, image: url })} />
          </div>
          {value.image && (
            <img src={value.image} alt="" className="w-20 h-20 object-contain bg-gray-50 rounded mt-2" />
          )}
        </Field>

        {/* Additional carousel images — slots 2-5 on the product detail carousel */}
        {[0, 1, 2, 3].map((i) => {
          const url = (value.detailImages || [])[i] || '';
          const updateAt = (newUrl) => {
            const arr = [...(value.detailImages || [])];
            while (arr.length < i) arr.push('');
            arr[i] = newUrl;
            // Drop trailing empties so the array stays clean
            while (arr.length && !arr[arr.length - 1]) arr.pop();
            onChange({ ...value, detailImages: arr });
          };
          return (
            <Field key={i} label={`Additional image ${i + 1}`} span={2}>
              <div className="flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => updateAt(e.target.value)}
                  placeholder="https://… or /uploads/images/…"
                  data-testid={`product-form-detail-image-${i}`}
                />
                <ImageUploadButton onUploaded={(u) => updateAt(u)} />
                {url && (
                  <button
                    type="button"
                    onClick={() => updateAt('')}
                    className="text-xs text-red-600 hover:text-red-700 px-2"
                  >Clear</button>
                )}
              </div>
              {url && (
                <img src={url} alt="" className="w-16 h-16 object-contain bg-gray-50 rounded mt-2" />
              )}
            </Field>
          );
        })}

        <Field label="Description" span={2}>
          <textarea
            value={value.description || ''}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            className="w-full p-2 border rounded text-sm" rows={3}
          />
        </Field>
        <Field label="Product URL Slug" span={2}>
          <Input value={value.productUrl || ''} onChange={(e) => onChange({ ...value, productUrl: e.target.value })} placeholder="e.g. bmw-518d-143-ps-pro" />
        </Field>
      </Section>

      <Section title="Installation Info (shown on product page)">
        <Field label="Installation PDF" span={2}>
          <div className="flex gap-2">
            <Input
              value={value.instructionsUrl || ''}
              onChange={(e) => onChange({ ...value, instructionsUrl: e.target.value })}
              placeholder="https://… or upload below"
              data-testid="product-form-pdf"
            />
            <PdfUploadButton onUploaded={(url) => onChange({ ...value, instructionsUrl: url })} />
          </div>
          {value.instructionsUrl && (
            <a href={value.instructionsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">
              ↗ Preview PDF
            </a>
          )}
        </Field>
        <Field label="Unbranded PDF URL" span={2}>
          <Input value={value.unbrandedInstructionsUrl || ''} onChange={(e) => onChange({ ...value, unbrandedInstructionsUrl: e.target.value })} placeholder="Optional unbranded version" />
        </Field>
        <Field label="Instructions ID">
          <Input value={value.instructionsId || ''} onChange={(e) => onChange({ ...value, instructionsId: e.target.value })} />
        </Field>
        <Field label="Connection Type">
          <Input value={value.connections || ''} onChange={(e) => onChange({ ...value, connections: e.target.value })} placeholder="e.g. Rail Only" />
        </Field>
        <Field label="Rail / MAP">
          <Input value={value.railMap || ''} onChange={(e) => onChange({ ...value, railMap: e.target.value })} />
        </Field>
        <Field label="Boost">
          <Input value={value.boost || ''} onChange={(e) => onChange({ ...value, boost: e.target.value })} />
        </Field>
        <Field label="CAM">
          <Input value={value.cam || ''} onChange={(e) => onChange({ ...value, cam: e.target.value })} />
        </Field>
      </Section>

      <Section title="Features (bullet points shown on product page)">
        <div className="md:col-span-2 space-y-2">
          {(value.features || []).map((f, idx) => (
            <div key={idx} className="flex gap-2">
              <Input value={f} onChange={(e) => updateFeature(idx, e.target.value)} placeholder={`Feature ${idx + 1}`} />
              <Button type="button" variant="outline" size="sm" onClick={() => removeFeature(idx)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addFeature} data-testid="add-feature-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Feature
          </Button>
        </div>
      </Section>
    </div>
  );
};

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState('all');
  const [make, setMake] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [categories, setCategories] = useState([]);
  const [makes, setMakes] = useState([]);
  const LIMIT = 25;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsAPI.getAll({
        page, limit: LIMIT,
        ...(search ? { search } : {}),
        ...(category !== 'all' ? { category } : {}),
        ...(make !== 'all' ? { make } : {}),
      });
      setProducts(data.products);
      setPages(data.pages);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, search, category, make]);

  useEffect(() => {
    Promise.all([categoriesAPI.getAll(), makesAPI.getAll()])
      .then(([c, m]) => { setCategories(c); setMakes(m); });
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Auto-open Add modal if arriving via ?new=1
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreating(true);
      setNewProduct(EMPTY_PRODUCT);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setSearch(''); setSearchInput(''); setCategory('all'); setMake('all'); setPage(1);
  };

  const handleSaveEdit = async () => {
    try {
      const { id, ...payload } = editing;
      await productsAPI.update(id, payload);
      toast.success('Product updated');
      setEditing(null);
      fetchProducts();
    } catch (e) {
      toast.error('Failed to update product');
    }
  };

  const handleCreate = async () => {
    try {
      if (!newProduct.name || !newProduct.make || !newProduct.category) {
        toast.error('Name, make and category are required');
        return;
      }
      await productsAPI.create(newProduct);
      toast.success('Product created');
      setCreating(false);
      setNewProduct(EMPTY_PRODUCT);
      fetchProducts();
    } catch (e) {
      toast.error('Failed to create product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await productsAPI.delete(productId);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="products-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Manage Products</h2>
          <p className="text-sm text-gray-500">{total.toLocaleString()} total products</p>
        </div>
        <Button
          size="lg"
          onClick={() => { setCreating(true); setNewProduct(EMPTY_PRODUCT); }}
          data-testid="add-product-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        >
          <Plus className="mr-2 h-5 w-5" /> Add New Product
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <form onSubmit={handleSearch} className="md:col-span-2 flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, make, model or SKU"
                  className="pl-9"
                  data-testid="products-search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="products-search-btn">Search</Button>
            </form>
            <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
              <SelectTrigger data-testid="products-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={make} onValueChange={(v) => { setMake(v); setPage(1); }}>
              <SelectTrigger data-testid="products-make-filter"><SelectValue placeholder="Make" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Makes</SelectItem>
                {makes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(search || category !== 'all' || make !== 'all') && (
            <button onClick={clearFilters} className="text-xs text-blue-600 hover:underline mt-2">Clear filters</button>
          )}
        </CardContent>
      </Card>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCreating(false)}>
          <Card className="w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between sticky top-0 bg-white pt-2 pb-3 -mt-2 border-b z-10">
                <h3 className="text-lg font-semibold">Add New Product</h3>
                <button onClick={() => setCreating(false)} className="text-gray-500 hover:text-gray-700" aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              <ProductForm value={newProduct} onChange={setNewProduct} categories={categories} makes={makes} />
              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="create-product-submit">
                  <Save className="h-4 w-4 mr-1" /> Create Product
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No products match your filters.</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">Make / Model</th>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium text-right">Price</th>
                    <th className="px-4 py-3 font-medium">Stock</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-3 max-w-md">
                        <div className="flex items-center gap-3">
                          <img src={p.image} alt="" className="w-10 h-10 object-contain bg-gray-50 rounded" />
                          <div>
                            <p className="font-medium line-clamp-1">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 font-mono text-xs" data-testid={`sku-${p.id}`}>
                        {p.sku || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.make} {p.model}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{p.category}</td>
                      <td className="px-4 py-3 text-right font-semibold">${p.price?.toFixed(2)} {p.currency}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${p.inStock ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {p.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button variant="outline" size="sm" onClick={() => setEditing({ ...p })} data-testid={`edit-${p.id}`}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="destructive" size="sm" className="ml-2" onClick={() => handleDelete(p.id)} data-testid={`delete-${p.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">
          Page {page} of {pages} · Showing {products.length} of {total.toLocaleString()}
        </p>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="pagination-next">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <Card className="w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between sticky top-0 bg-white pt-2 pb-3 -mt-2 border-b z-10">
                <h3 className="text-lg font-semibold">Edit {editing.id}</h3>
                <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
              </div>
              <ProductForm value={editing} onChange={setEditing} categories={categories} makes={makes} />
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSaveEdit} data-testid="save-edit-btn"><Save className="h-4 w-4 mr-1" /> Save changes</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;
